import {KColorInput} from "@feature/kcolor/kcolor-input";
import evaluateBlockDependencies from "@utils/evaluateBlockDependencies";
import ConfiguratorContext from "@components/ConfiguratorContext";
import {useContext} from "react";

const ConfiguratorSurveyKColorInput = (
  {
    value,
    onChange,
    disabled,
    question,
    responses,
  }
) => {

  const context = useContext(ConfiguratorContext);

    /*
  HUBSPOT BUG FIX:
  whenever a kcolor question products dependencies are re-defined in a modular service
  the metata_ (which contains the products dependencies) must be retrieved from crossData
  instead of using the one present in the question object, their kcolor product dependencies might differ.
   */
  const eventuallyRetrieveMetadataFromCrossServices = () => {
    if (question.service_block_id && context.crossData) {
      const cd =
        context.crossData[question.service_block_id]?.[question.id];
      if (cd?.metadata_) {
        return cd.metadata_;
      }
    }
    return question.metadata_;
  }
  const metadata = eventuallyRetrieveMetadataFromCrossServices();

  const selectableProducts = Object.entries(metadata?.products_dependencies ?? {}).reduce(
    (productsAcc, [productCode, productData]) => {
      const {disabled, dependencies, ...packages} = productData;

      const originalMetadata = question.metadata_
      /*
      retrieve original dependencies from metadata_ in case the current product dependencies
      contain an original_expression placeholder, and replace it with the original dependencies!
       */
      const fixedDependencies = dependencies.reduce((acc, e) => {
        if (e.startsWith("{")) {
          const parsed = JSON.parse(e);
          if (parsed?.original === 'original_expression') {
            return [...acc, ...originalMetadata?.products_dependencies?.[productCode].dependencies]
          }
        }
        return [...acc, e]
      }, [])
      // 1. Check if the product is selectable in its entirety
      const productSelectable = !disabled && (
        !fixedDependencies?.length ||
        evaluateBlockDependencies(fixedDependencies, {
          ...context,
          responses: responses
        })
      );

      if (!productSelectable) return productsAcc;

      // 2. Collect selectable packages
      const selectablePackages = Object.entries(packages).reduce(
        (packagesAcc, [packageQty, packageData]) => {
          const {disabled, dependencies} = packageData;
          /*
          retrieve original dependencies from metadata_ in case the current package dependencies
          contain an original_expression placeholder, and replace it with the original dependencies!
           */
          const fixedPackageDependencies = dependencies?.reduce((acc, e) => {
            if (e.startsWith("{")) {
              const parsed = JSON.parse(e);
              if (parsed?.original === 'original_expression') {
                return [...acc, ...originalMetadata?.products_dependencies?.[productCode]?.[packageQty]?.dependencies]
              }
            }
            return [...acc, e]
          }, [])
          const packageSelectable = !disabled && (
            !dependencies?.length ||
            evaluateBlockDependencies(fixedPackageDependencies, {
              ...context,
              responses: responses
            })
          );
          if (packageSelectable) return [...packagesAcc, Number(packageQty)];
          return packagesAcc;
        },
        []
      );
      // 3. Collect packages that are disabled
      const disabledPackages = Object.entries(packages).reduce(
        (packagesAcc, [packageQty, packageData]) => {
          const {disabled, dependencies} = packageData;
          if (disabled) {
            return [...packagesAcc, Number(packageQty)];
          } else {
            return packagesAcc;
          }
        },
        []
      );

      // If all packages are disabled, skip this product
      if (disabledPackages.length === Object.keys(packages).length) {
        return productsAcc;
      }

      // If no packages are selectable due to dependencies evaluation, then return all packages that
      // are not disabled. This ensures that at least some packages are selectable for the product, but
      // only those that are not explicitly disabled.
      if (
        selectablePackages.length === 0
      ) {
        return {...productsAcc, [productCode]: Object.keys(packages).filter(qty => !disabledPackages.includes(Number(qty))).map(Number)};
      } else {
        return {...productsAcc, [productCode]: selectablePackages};
      }
    },
    {}
  ) ?? {};

  return (
    <KColorInput
      value={value}
      onChange={onChange}
      selectableProducts={selectableProducts}
      selectableColorsExceptions={metadata?.colorsException}
      inSurvey={true}
      disabled={disabled}
    />
  );
}

export default ConfiguratorSurveyKColorInput;